import React, { useEffect, useState, useContext } from 'react';
import { useForm } from "react-hook-form";
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl'
import ReCAPTCHA from "react-google-recaptcha";
import Theme from '../Theme';
import { Select } from '@mui/material';
import ConfigContext from "../contexts/ConfigContext";
import axios from 'axios';
   
export default function SearchForm() {
  const { register, trigger, handleSubmit, formState: { errors, isValid }, watch, setValue } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange"
  });

  const config = useContext(ConfigContext);
  const [errorMessage, setErrorMessage] = useState('');
  const acceptTerms = watch("acceptTerms");
  const recaptchaRef = React.useRef();
 
  const formSubmit = async (data) => {
    console.log("data", data);

    await axios
    .post(`${config.SEARCHAPI_URL}/api/search`, data)
    .then(function (response) {
      const message = response.data.message;

      if (message !== '') {
        window.location.href = `${config.SEARCHAPI_URL}/api/pdf1095?resourceId=${encodeURIComponent(message)}`;
        return;
      } 
      onError();
    })
    .catch(function (error) {
      console.log(error);
      onError();
    });
  };

  function onError() {
    setErrorMessage('Search Failed');
    recaptchaRef.current.reset();
    setValue("captchaToken", "");
    trigger("captchaToken");
  }

  const onChange = (token) => {
    setValue("captchaToken", token);
    trigger("captchaToken");
  };
  
  useEffect(() => {
    register("captchaToken", { required: true });
  });

  return (
    <React.Fragment>
      <Typography hidden={errorMessage === ''} variant="subtitle2" sx={{...Theme.typography.button, 'color': Theme.palette.error.main, 'marginBottom': Theme.spacing(2) }}>
      Based on the information you have entered, no 1095B is found. If you believe you should have found a form or you want to have a copy mailed to you please call 855-673-0145 or email Request1095b@fssa.in.gov.
      </Typography>
      <Typography variant="h6" gutterBottom>
        Find 1095 B Form
      </Typography>
      <Typography variant="body2" gutterBottom>
      Enter data exactly as reported to the Indiana Family and Social Services Administration.
      </Typography>
      <form
            method="POST"
            onSubmit={handleSubmit(formSubmit)}
            autoComplete="off"
          >
              <Grid item xs={12} sm={9}>
                  <FormControl variant="standard" fullWidth>
                      <InputLabel id="Tax-label">Tax Year *</InputLabel>
                      <Select
                          required
                          labelId="Tax-label"
                          id="TaxCode"
                          name="TaxCode"
                          label="Tax Year *"
                          autoWidth
                          defaultValue="2024"
                          error={Boolean(errors?.TaxCode)}
                          {...register("TaxCode", {
                              required: "Tax Year must be selected"
                          })}
                      >
                          <MenuItem value="2024">2024</MenuItem>
                      </Select>
                  </FormControl>
              </Grid>
          <Grid container spacing={3} sx={{ mt: 1}}>
            <Grid item xs={12}>
              <TextField
                required
                id="lastName"
                name="lastName"
                label="Last Name"
                fullWidth
                variant="standard"
                error={Boolean(errors?.lastName)}
                helperText={errors?.lastName?.message}
                InputLabelProps={{
                  shrink: true,
                }}
                {...register("lastName", {
                  required: "Last name must be entered",
                  pattern: {
                      value: /^\s*[a-zA-Z- ']+\s*$/,
                    message: "Last Name must be a valid string",
                    },
                  maxLength: {
                    value: 30,
                    message: "Name can't be greater than 30 characters",
                  },
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                type="password"
                id="ssnOrTin"
                name="ssnOrTin"
                label="SSN/TIN"
                fullWidth
                variant="standard"
                error={Boolean(errors?.ssnOrTin)}
                helperText={errors?.ssnOrTin?.message}
                InputLabelProps={{
                  shrink: true
                }}
                {...register("ssnOrTin", {
                  required: "SSN/TIN must be entered",
                  pattern: {
                    value: /^\d{9}$/,
                    message: "SSN/TIN must be 9 digits"
                  }
              })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                id="dob"
                name="dob"
                label="Date of Birth"
                fullWidth
                variant="standard"
                type="date"
                error={Boolean(errors?.dob)}
                helperText={errors?.dob?.message}
                InputLabelProps={{
                  shrink: true,
                }}
                {...register("dob", {
                  required: "Date of birth must be entered or selected from calendar",
              })}                
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                id="city"
                name="city"
                label="City"
                fullWidth
                variant="standard"
                error={Boolean(errors?.city)}
                helperText={errors?.city?.message}
                InputLabelProps={{
                  shrink: true,
                }}
                {...register("city", {
                  required: "City must be entered",
                  pattern: {
                      value: /^\s*([a-zA-Z]+)(.?\s?-?)([a-zA-Z]+)\s*$/,
                    message: "City must be a valid name"
                  }
              })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
            <FormControl variant="standard" fullWidth>
              <InputLabel id="state-label">State *</InputLabel>
              <Select
                required         
                labelId="state-label"
                id="stateCode"
                name="stateCode"
                label="State *"
                autoWidth
                defaultValue="IN"   
                error={Boolean(errors?.stateCode)}
                {...register("stateCode", {
                  required: "State Code must be selected"
                })}
              >
								<MenuItem value="AL">AL - Alabama</MenuItem>
								<MenuItem value="AK">AK - Alaska</MenuItem>
								<MenuItem value="AS">AS - American Samoa</MenuItem>
								<MenuItem value="AZ">AZ - Arizona</MenuItem>
								<MenuItem value="AR">AR - Arkansas</MenuItem>
								<MenuItem value="CA">CA - California</MenuItem>
								<MenuItem value="CO">CO - Colorado</MenuItem>
								<MenuItem value="CT">CT - Connecticut</MenuItem>
								<MenuItem value="DE">DE - Delaware</MenuItem>
								<MenuItem value="DC">DC - District of Columbia</MenuItem>
								<MenuItem value="FM">FM - Federated Stated of Micronesia</MenuItem>
								<MenuItem value="FL">FL - Florida</MenuItem>
								<MenuItem value="GA">GA - Georgia</MenuItem>
								<MenuItem value="GU">GU - Guam</MenuItem>
								<MenuItem value="HI">HI - Hawaii</MenuItem>
								<MenuItem value="ID">ID - Idaho</MenuItem>
								<MenuItem value="IL">IL - Illinois</MenuItem>
								<MenuItem value="IN">IN - Indiana</MenuItem>
								<MenuItem value="IA">IA - Iowa</MenuItem>
								<MenuItem value="KS">KS - Kansas</MenuItem>
								<MenuItem value="KY">KY - Kentucky</MenuItem>
								<MenuItem value="LA">LA - Louisiana</MenuItem>
								<MenuItem value="ME">ME - Maine</MenuItem>
								<MenuItem value="MH">MH - Marshall Islands</MenuItem>
								<MenuItem value="MD">MD - Maryland</MenuItem>
								<MenuItem value="MA">MA - Massachusetts</MenuItem>
								<MenuItem value="MI">MI - Michigan</MenuItem>
								<MenuItem value="MN">MN - Minnesota</MenuItem>
								<MenuItem value="MS">MS - Mississippi</MenuItem>
								<MenuItem value="MO">MO - Missouri</MenuItem>
								<MenuItem value="MT">MT - Montana</MenuItem>
								<MenuItem value="NE">NE - Nebraska</MenuItem>
								<MenuItem value="NV">NV - Nevada</MenuItem>
								<MenuItem value="NH">NH - New Hampshire</MenuItem>
								<MenuItem value="NJ">NJ - New Jersey</MenuItem>
								<MenuItem value="NM">NM - New Mexico</MenuItem>
								<MenuItem value="NY">NY - New York</MenuItem>
								<MenuItem value="NC">NC - North Carolina</MenuItem>
								<MenuItem value="ND">ND - North Dakota</MenuItem>
								<MenuItem value="MP">MP - Northern Mariana Islands</MenuItem>
								<MenuItem value="OH">OH - Ohio</MenuItem>
								<MenuItem value="OK">OK - Oklahoma</MenuItem>
								<MenuItem value="OR">OR - Oregon</MenuItem>
								<MenuItem value="PW">PW - Palau</MenuItem>
								<MenuItem value="PA">PA - Pennsylvania</MenuItem>
								<MenuItem value="PR">PR - Puerto Rico</MenuItem>
								<MenuItem value="RI">RI - Rhode Island</MenuItem>
								<MenuItem value="SC">SC - South Carolina</MenuItem>
								<MenuItem value="SD">SD - South Dakota</MenuItem>
								<MenuItem value="TN">TN - Tennessee</MenuItem>
								<MenuItem value="TX">TX - Texas</MenuItem>
								<MenuItem value="UT">UT - Utah</MenuItem>
								<MenuItem value="VT">VT - Vermont</MenuItem>
								<MenuItem value="VI">VI - Virgin Islands</MenuItem>
								<MenuItem value="VA">VA - Virginia</MenuItem>
								<MenuItem value="WA">WA - Washington</MenuItem>
								<MenuItem value="WV">WV - West Virginia</MenuItem>
								<MenuItem value="WI">WI - Wisconsin</MenuItem>
								<MenuItem value="WY">WY - Wyoming</MenuItem>
								<MenuItem value="AA">AA - Armed Forces Americas</MenuItem>
								<MenuItem value="AE">AE - Armed Forces Africa</MenuItem>
								<MenuItem value="AE">AE - Armed Forces Canada</MenuItem>
								<MenuItem value="AE">AE - Armed Forces Europe</MenuItem>
								<MenuItem value="AE">AE - Armed Forces Middle East</MenuItem>
								<MenuItem value="AP">AP - Armed Forces Pacific</MenuItem>
              </Select>
              </FormControl>
                  </Grid>

                 
                 
            <Grid item xs={12} sm={2}>
              <TextField
                required
                id="zipCode"
                name="zipCode"
                label="zip Code"
                fullWidth
                autoComplete="postal-code"
                variant="standard"
                error={Boolean(errors?.zipCode)}
                helperText={errors?.zipCode?.message}
                InputLabelProps={{
                  shrink: true,
                }}
                {...register("zipCode", {
                  required: "Zip Code must be entered",
                  pattern: {
                    value: /^\d{5}$/,
                    message: "Zip Code must be five digits"
                  }
              })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="acceptTerms" value="yes" {...register("acceptTerms")} />}
                label={<Typography 
                  variant="caption"
                  >
                  By checking this box, I consent to the Terms of Use and Privacy Statement associated with this site.
                  </Typography>}
              />
            </Grid>
            <Grid item>
                  <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={config.RECAPTCHA_KEY}  
                        onChange={onChange}
                        >
                  </ReCAPTCHA>
            </Grid>
          </Grid>                    
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              sx={{ mt: 3, ml: 1 }}
              type="submit"
              disabled={!(isValid && acceptTerms)} 
            >
              Find Form
            </Button>
          </Box>
      </form>
    </React.Fragment>
  );
}